import styled from '@emotion/styled';
import moment from 'moment';
import React from 'react';
import {useTranslation} from 'react-i18next';
import {
  ECancelButton,
  EDeleteButton,
  EForm,
  EFormButtonRow,
  ESubmitButton,
} from 'webapp-lib/Components';
import {mixinFlexboxRow, mixinValidationErrorMessage} from 'webapp-lib/styling';
import {TIME_FORMAT} from 'webapp-lib/utils';

import {CasusAutocomplete} from '#/Autocomplete';
import {Dialog} from '#/Dialog/Dialog';
import {
  EFTextArea,
  EFTimeInput,
  requiredAndNonWhitespaceRule,
  requiredRule,
  useForm,
} from '_/utilities/form';

import {
  EventApiWithTimEntryProps,
  TimeEntry,
  TimeEntryCasus,
} from '../timetracker.types';
import {createTimeEntryCasus} from '../utilities/casus.util';

enum EditTimeEntryFormFields {
  CasusId = 'casusId',
  Title = 'title',
  Description = 'description',
  StartTime = 'startTime',
  EndTime = 'endTime',
}

type EditTimeEntryFormInputs = {
  [EditTimeEntryFormFields.CasusId]?: string;
  [EditTimeEntryFormFields.Title]: string;
  [EditTimeEntryFormFields.Description]?: string;
  [EditTimeEntryFormFields.StartTime]: string;
  [EditTimeEntryFormFields.EndTime]: string;
};

const isValidTimeSlot = (startTime: string, endTime: string): boolean => {
  const start = moment(startTime, TIME_FORMAT);
  const end = moment(endTime, TIME_FORMAT);
  if (!start.isValid() || !end.isValid()) {
    return false;
  }

  const durationHours = moment.duration(end.diff(start)).asHours();
  return durationHours >= 0 && durationHours <= 24;
};

export function EditTimeEntryDialog({
  data,
  isOpen,
  onClose,
  onEdit,
  onDelete,
}: {
  data: EventApiWithTimEntryProps;
  isOpen: boolean;
  onEdit: (event: TimeEntry) => void | Promise<void>;
  onDelete: (event: EventApiWithTimEntryProps) => void;
  onClose: () => void;
}): React.ReactElement {
  const {t} = useTranslation('timetracker');

  const {control, formState, clearErrors, getValues, handleSubmit} =
    useForm<EditTimeEntryFormInputs>({
      defaultValues: {
        casusId: data.extendedProps.casus?.id || '',
        title: data.title || '',
        description: data.extendedProps?.description || '',
        startTime: moment(data.start).format(TIME_FORMAT),
        endTime: moment(data.end).format(TIME_FORMAT),
      },
      onSubmit: async ({casusId, title, description, startTime, endTime}) => {
        let casus: TimeEntryCasus | undefined;
        if (casusId) {
          casus = await createTimeEntryCasus(casusId);
        }

        const applyTimeToDate = (date: Date, timeString: string) => {
          const time = moment(timeString, TIME_FORMAT);
          return moment(date)
            .set({hour: time.hour(), minute: time.minute()})
            .toDate();
        };

        const result: TimeEntry = {
          id: data.id,
          title,
          description,
          casus,
          start: applyTimeToDate(data.start!, startTime),
          end: applyTimeToDate(data.end!, endTime),
        };

        await onEdit(result);
        onClose();
      },
    });

  const clearTimeFieldsErrors = () => {
    clearErrors(EditTimeEntryFormFields.StartTime);
    clearErrors(EditTimeEntryFormFields.EndTime);
  };

  const timeFieldsError =
    formState.errors[EditTimeEntryFormFields.StartTime] ||
    formState.errors[EditTimeEntryFormFields.EndTime];

  const startTimeRules = {
    ...requiredRule,
    validate: (startTime: string) => {
      clearTimeFieldsErrors();

      const {endTime} = getValues();
      return isValidTimeSlot(startTime, endTime);
    },
  };

  const endTimeRules = {
    ...requiredRule,
    validate: (endTime: string) => {
      clearTimeFieldsErrors();

      const {startTime} = getValues();
      return isValidTimeSlot(startTime, endTime);
    },
  };

  return (
    <Dialog
      headerText={t('components.timeEntries.title')}
      isShown={isOpen}
      size='tiny'
      onClose={onClose}
    >
      <EForm onSubmit={handleSubmit}>
        <EFTextArea
          control={control}
          label={t('attributes.title')}
          minRows={2}
          name={EditTimeEntryFormFields.Title}
          rules={requiredAndNonWhitespaceRule}
          autoFocus
        />
        <CasusAutocomplete
          control={control}
          label={t('attributes.casus')}
          name={EditTimeEntryFormFields.CasusId}
          searchFiltersPersistenceKey='timetrackerTimeEntryForm'
        />
        <EFTextArea
          control={control}
          label={t('attributes.description')}
          minRows={4}
          name={EditTimeEntryFormFields.Description}
        />

        <EventTimeRow>
          <EFTimeInput
            control={control}
            label={t('attributes.start')}
            name={EditTimeEntryFormFields.StartTime}
            rules={startTimeRules}
          />
          <span className='dash'>&mdash;</span>
          <EFTimeInput
            control={control}
            label={t('attributes.end')}
            name={EditTimeEntryFormFields.EndTime}
            rules={endTimeRules}
          />
        </EventTimeRow>
        {timeFieldsError ? (
          <ValidationMessage>
            {t('validations.endTimeMustBeAfterStartTime')}
          </ValidationMessage>
        ) : null}

        <EFormButtonRow>
          <ECancelButton disabled={formState.isSubmitting} onClick={onClose} />
          <ButtonRow>
            <EDeleteButton onClick={() => onDelete(data)} />
            <ESubmitButton isSubmitting={formState.isSubmitting} />
          </ButtonRow>
        </EFormButtonRow>
      </EForm>
    </Dialog>
  );
}

export const ValidationMessage = styled.p`
  ${mixinValidationErrorMessage}
`;

const EventTimeRow = styled.div`
  ${mixinFlexboxRow};
  gap: var(--gutter-little);

  .dash {
    height: var(--input-min-height);
    line-height: var(--input-min-height);
    align-self: flex-end;
    vertical-align: middle;
  }
`;

const ButtonRow = styled.span`
  ${mixinFlexboxRow};
  gap: var(--gutter-little);
  justify-content: space-between;
`;
